import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { eqlQuery } from "../../../../apis/apiEQL";
import { getReports } from "../../../../apis/apiReports";

// "Open" statuses: actively-handled reports (excludes draft, closed, closing_process).
const OPEN_STATUSES = ["new", "pending", "process", "finish"];

const openStatusCondition = { field: "status__current", operator: "in", value: OPEN_STATUSES };

// Count sources. EQL is used when the condition maps to stored fields; the
// report-list endpoint is used when it depends on computed fields the backend
// only exposes there (e.g. open_days / SLA thresholds for "expired").
const eqlCount = (filter) => async () => {
  const resp = await eqlQuery({ fields: ["folio"], filters: filter, limit: 1 });
  return resp?.metadata?.total ?? 0;
};
const listCount = (params) => async () => {
  const data = await getReports({ ...params, limit: 1 });
  return data?.count ?? 0;
};

// One entry per dashboard alert type. Each is a single summary card whose count
// is fetched lazily; clicking it opens the matching filtered report list.
// `severity` drives the card color and its position in the deck (high=red on
// top, medium=yellow, low=green at the back).
const ALERT_DEFS = [
  {
    id: "expired",
    severity: "high",
    // open_days >= SLA threshold; the backend already scopes this to not-closed.
    getCount: listCount({ expiration_status: "expired" }),
    route: "/reports/all/?expiration_status=expired",
    singularKey: "expired_singular",
    pluralKey: "expired_plural",
  },
  {
    id: "to-expire",
    severity: "medium",
    // Ventana de aviso previa al vencimiento: el backend la resuelve con los dos
    // valores de Configuración > General — "Días para alertar" (report_to_expire)
    // y "Días de vencimiento" (expired_report) — como
    // report_to_expire <= open_days < expired_report, sobre reportes no cerrados.
    // Si el tenant no tiene ambos configurados, el backend no aplica el filtro.
    getCount: listCount({ expiration_status: "to_expire" }),
    route: "/reports/all/?expiration_status=to_expire",
    singularKey: "to_expire_singular",
    pluralKey: "to_expire_plural",
  },
  {
    id: "severe-open",
    severity: "high",
    // severity is a nested dict { path: "type::hight" }.
    getCount: eqlCount({
      AND: [{ field: "severity__path", operator: "eq", value: "type::hight" }, openStatusCondition],
    }),
    // kind_of_severity maps to severity__path on the backend.
    route: "/reports/all/?kind_of_severity=type::hight",
    singularKey: "severe_open_singular",
    pluralKey: "severe_open_plural",
  },
  {
    id: "blocked-open",
    severity: "medium",
    // locked is a nested dict { is_locked: true }.
    getCount: eqlCount({
      AND: [{ field: "locked__is_locked", operator: "eq", value: true }, openStatusCondition],
    }),
    // indicator_is_locked is stripped to is_locked by the report list backend.
    route: "/reports/all/?indicator_is_locked=true",
    singularKey: "blocked_open_singular",
    pluralKey: "blocked_open_plural",
  },
  {
    id: "closing-in-process",
    severity: "low",
    // Closed status but no concluded closure type yet ("en proceso de cierre").
    // The backend owns the isnull/not-in logic, so count via the list endpoint.
    getCount: listCount({ close_type: "in_process" }),
    route: "/reports/all/?close_type=in_process",
    singularKey: "closing_singular",
    pluralKey: "closing_plural",
  },
];

/**
 * useStaticAlerts — dashboard alert summaries (one card per alert type), not a
 * per-report list. Each card's count is fetched lazily (only the total, never
 * the rows); clicking it opens the filtered report list.
 *
 * @returns {{ alerts: Array<{id,title,detail,severity,route}>, loading: boolean }}
 */
export const useStaticAlerts = () => {
  const [t] = useTranslation("dashboard");
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    (async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          ALERT_DEFS.map(async (def) => {
            try {
              return { def, count: await def.getCount() };
            } catch (e) {
              console.error(`useStaticAlerts: ${def.id} count failed`, e);
              return { def, count: 0 };
            }
          })
        );
        if (!active) return;

        setAlerts(
          results
            .filter(({ count }) => count > 0)
            .map(({ def, count }) => ({
              id: def.id,
              severity: def.severity,
              title: `${count} ${
                count === 1
                  ? t(`static_dashboard.${def.singularKey}`)
                  : t(`static_dashboard.${def.pluralKey}`)
              }`,
              detail: t("static_dashboard.alert_view_list"),
              route: def.route,
            }))
        );
      } catch (e) {
        console.error("useStaticAlerts: failed", e);
        if (active) setAlerts([]);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [t]);

  return { alerts, loading };
};

export default useStaticAlerts;
